import { AddIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Select,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useState } from "react";
import { FaRegCompass } from "react-icons/fa";
import InfiniteScroll from "react-infinite-scroll-component";
import { useInfiniteQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { usePremium } from "../../../contexts/Premium";
import { franchiseMapIndex, titleMapIndex } from "../../../core/constants";
import { getMyReadingOrders } from "../../../services/readingOrdersApi";
import Spinner from "../../../shared/components/Spinner";
import MyReadingOrderCard from "../components/MyReadingOrderCard";
import MyOrdersTease from "./MyOrdersTease";
import PublishMyOrderModal from "../components/PublishMyOrderModal";

const MyOrders = () => {
  const navigate = useNavigate();
  const { isPremium } = usePremium();
  const paywallSort = isPremium ? undefined : "free_first";
  const [franchise, setFranchise] = useState(0);
  const [sortBy, setSortBy] = useState("a-z");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedReadingOrder, setSelectedReadingOrder] = useState(null);

  const { isLoading, data, isSuccess, fetchNextPage, hasNextPage } =
    useInfiniteQuery(
      ["myReadingOrders", franchise, sortBy],
      ({ pageParam = 1 }) =>
        getMyReadingOrders(franchise, 0, sortBy, pageParam, paywallSort),
      {
        getNextPageParam: (pageParam, allPages) => {
          if (pageParam?.meta.currentPage === pageParam?.meta.lastPage) {
            return undefined;
          }
          return pageParam?.meta?.currentPage + 1;
        },
        enabled: !!isPremium,
      }
    );

  const openPublishModal = (readingOrder) => {
    onOpen();
    setSelectedReadingOrder(readingOrder);
  };

  let queryData = [];

  if (!isLoading && isSuccess) {
    queryData = [...data.pages.flatMap((group, i) => group.data)];
  }

  const groupedQueryData = queryData
    .reduce((acc, readingOrder) => {
      const key = parseInt(readingOrder?.type, 10);
      if (!acc[key - 1]) {
        acc[key - 1] = { title: key, data: [] };
      }
      acc[key - 1].data.push(readingOrder);
      return acc;
    }, [])
    .filter((group) => group);

  if (!isPremium) {
    return <MyOrdersTease />;
  }

  return (
    <Container maxW="container.md">
      <Box mt="6"></Box>
      <Center mb="6">
        <Flex direction="column" alignItems="flex-start">
          <Heading as="h2" mt="6" mb="1">
            My Orders
          </Heading>
          <Text color="gray.400">
            Create, manage, and share your own reading orders.
          </Text>
        </Flex>
        <Button
          onClick={() => navigate("/my-orders/new")}
          ml="auto"
          leftIcon={<AddIcon />}
          colorScheme="blue"
          textColor="white"
        >
          Create new reading order
        </Button>
      </Center>
      <Flex gap={4} mb={8}>
        <FormControl flex="1">
          <FormLabel>Franchise:</FormLabel>
          <Select onChange={(e) => setFranchise(e.target.value)} mr={4}>
            <option value="0">All franchises</option>
            {Object.keys(franchiseMapIndex).map((franchise, index) => (
              <option value={franchise} key={franchise}>
                {franchiseMapIndex[franchise]}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl flex="1">
          <FormLabel>Sort by:</FormLabel>
          <Select onChange={(e) => setSortBy(e.target.value)} mr={4}>
            <option value="a-z">Title (A to Z)</option>
            <option value="modified">Last modified</option>
            <option value="newest">Newest to oldest</option>
            <option value="oldest">Oldest to newest</option>
          </Select>
        </FormControl>
      </Flex>
      {isLoading ? (
        <Spinner />
      ) : (
        <div>
          <InfiniteScroll
            pageStart={1}
            loadMore={() => fetchNextPage()}
            dataLength={queryData.length}
            height="auto"
            hasMore={hasNextPage}
            loader={
              <Center key="loader">
                <Button size="sm" variant="outline" w="100%">
                  Loading more orders...
                </Button>
              </Center>
            }
          >
            {groupedQueryData.map((group, index) => (
              <Box key={`group-${group.title}-${index}`}>
                <Text
                  fontSize="sm"
                  color="gray.400"
                  mt="6"
                  mb="2"
                  casing="uppercase"
                >
                  {titleMapIndex[group.title]}
                </Text>
                {group.data.map((readingOrder, orderIndex) => (
                  <MyReadingOrderCard
                    key={`order-${readingOrder.id}-${orderIndex}`}
                    url={`/my-orders/${readingOrder.id}/edit-order`}
                    openPublishModal={openPublishModal}
                    readingOrder={readingOrder}
                  />
                ))}
              </Box>
            ))}
          </InfiniteScroll>
        </div>
      )}
      {queryData.length === 0 && (
        <Center p="30" flexDirection="column">
          <FaRegCompass size="60px" color="#666" />
          {Number(franchise) !== 0 ? (
            <>
              <Text my="3">No reading orders found for this franchise.</Text>
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={() => {
                  setFranchise(0);
                }}
              >
                Clear filters
              </Button>
            </>
          ) : (
            <>
              <Text my="3">You have not created reading orders yet.</Text>
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={() => navigate("/my-orders/new")}
              >
                Create a new reading order
              </Button>
            </>
          )}
        </Center>
      )}
      <PublishMyOrderModal
        isOpen={isOpen}
        onClose={onClose}
        readingOrder={selectedReadingOrder}
      />
    </Container>
  );
};

export default MyOrders;
